import { useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { createTransitionControllerContext } from '../context/TransitionControllersContext';
import useIsomorphicLayoutEffect from '../../../hooks/useIsomorphicLayoutEffect';
import type { TransitionController } from '../../core-transition-component';
import { onlyElements } from '../lib/onlyElements';
import { hasDifference, onlyNewElements } from '../lib/onlyNewElements';
import { transitionOutTransitionControllers } from '../lib/transitionOutTransitionControllers';

const { TransitionControllersContext, useTransitionControllers } =
  createTransitionControllerContext();

export const useTransitionPresenceTransitionControllers = useTransitionControllers;

export interface TransitionPresenceProps {
  children: ReactNode;
  crossFlow?: boolean;
}

/**
 * Will transition out old children before replacing with new children
 */
export function TransitionPresence({ children, crossFlow }: TransitionPresenceProps): ReactElement {
  const didMountRef = useRef(false);
  const elements = onlyElements(children);
  const transitionControllers = useMemo(() => new Set<TransitionController>(), []);

  const [displayChildren, setDisplayChildren] = useState<{ items: Array<ReactElement<unknown>> }>({
    items: elements,
  });

  const newItems = onlyNewElements(elements, displayChildren.items);
  const transitionPromise = useRef<Promise<void>>(undefined);
  const domUpdatedPromise = useRef<Promise<void>>(undefined);
  const domUpdatedResolver = useRef<() => void>(undefined);
  const oldItems = useRef<Array<ReactElement<unknown>>>(undefined);
  const oldTransitionOutItems = useRef<Array<ReactElement<unknown>>>(undefined);

  useIsomorphicLayoutEffect(() => {
    let aborted = false;

    if (didMountRef.current) {
      if ((newItems.length === 0 && crossFlow) || !hasDifference(elements, displayChildren.items)) {
        // In case we removed the item from the DOM, but while it was still transitioning-out we added it back to the DOM,
        // so there was no 'difference' in children (we want to re-trigger the useMountTransition)
        if (
          !hasDifference(elements, displayChildren.items) &&
          domUpdatedPromise.current &&
          oldItems.current
        ) {
          (async () => {
            // wait till new-dom is rendered promise.
            await domUpdatedPromise.current;
            domUpdatedPromise.current = undefined;

            // Compare again and update directly
            if (!hasDifference(elements, oldItems.current as Array<ReactElement<unknown>>)) {
              setDisplayChildren({
                items: elements,
              });
            }
          })();
        }

        return;
      }

      (async () => {
        domUpdatedPromise.current = new Promise((resolver) => {
          domUpdatedResolver.current = resolver;
        });

        if (transitionPromise.current) {
          await transitionPromise.current;
          transitionPromise.current = undefined;
        }

        let transitionOutPromise = Promise.resolve();

        if (
          displayChildren.items.length > 0 &&
          (!oldTransitionOutItems.current ||
            hasDifference(
              oldTransitionOutItems.current as Array<ReactElement<unknown>>,
              displayChildren.items,
            ))
        ) {
          oldTransitionOutItems.current = displayChildren.items;
          transitionOutPromise = transitionOutTransitionControllers(transitionControllers);
        }

        // Abort flow when match changes while awaiting promises
        if (aborted) {
          return;
        }

        if (crossFlow) {
          // Add the new item(s) to current-layout
          setDisplayChildren((previous) => ({
            items: [...previous.items, ...onlyNewElements(elements, previous.items)],
          }));
        }

        transitionPromise.current = transitionOutPromise;
        await transitionOutPromise;
        transitionPromise.current = undefined;

        if (hasDifference(elements, displayChildren.items)) {
          oldItems.current = displayChildren.items;

          setDisplayChildren({
            items: elements,
          });

          setTimeout(() => {
            oldTransitionOutItems.current = undefined;
            if (domUpdatedResolver.current) domUpdatedResolver.current();
          }, 0);
        }
      })();
    } else {
      didMountRef.current = true;
    }

    return () => {
      aborted = true;
    };
  }, [children]);

  return (
    <TransitionControllersContext.Provider value={transitionControllers}>
      {displayChildren?.items.map((child) => child)}
    </TransitionControllersContext.Provider>
  );
}
